import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import fondoLogin from '@/images/fondoLogin2.png';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const loggedUser = await login(email, password);
      if (loggedUser) {
        // Decidir ruta por rol directamente con el usuario devuelto
        const rolesArr = Array.isArray(loggedUser?.roles) ? loggedUser.roles.map((r) => String(r || '').toLowerCase().trim()) : [];
        const primary = String(loggedUser?.rol ?? loggedUser?.role ?? '').toLowerCase().trim();
        const isOperario = rolesArr.includes('operario') || rolesArr.includes('operador') || primary === 'operario' || primary === 'operador';
        const isCliente = primary === 'cliente';
        const isConductor = primary === 'conductor';
        const isClientePrendas = primary === 'cliente_prendas';
        // Servicios públicos: ir directo al dashboard del centro asignado.
        // Si no tiene lavandería, caer al listado general como fallback.
        const isServiciosPublicos = primary === 'servicios_publicos';
        const lavId = loggedUser?.lavanderia?.id || loggedUser?.lavanderia?._id;
        const dest = isOperario ? '/operario-dashboard'
          : isCliente ? '/cliente-dashboard'
          : isConductor ? '/conductor'
          : isClientePrendas ? '/mis-prendas'
          : isServiciosPublicos ? (lavId ? `/centros-lavado/${lavId}` : '/centros-lavado')
          : '/dashboard';
        navigate(dest, { replace: true });
      }
    } catch (error) {
      console.error('Error during login:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const backgroundStyle = {
    backgroundImage: `url(${fondoLogin})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={backgroundStyle}
    >
      {/* Decoración de fondo */}
      <div className="hidden dark:block pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-20 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-fuchsia-500/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(transparent_1px,_rgba(255,255,255,0.04)_1px)] [background-size:16px_16px]" />
      </div>

      <Card className="w-full max-w-md shadow-2xl border border-slate-200/60 dark:border-0 backdrop-blur supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-white/10 bg-white/70 dark:bg-white/10 text-slate-900 dark:text-white">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Iniciar sesión</CardTitle>
          <CardDescription className="text-center text-slate-600 dark:text-slate-200/80">
            Ingresa tus credenciales para acceder al sistema
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 dark:text-slate-200">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                placeholder="usuario@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-white dark:bg-white/5 text-slate-900 dark:text-white placeholder:text-slate-400 border border-slate-300 dark:border-white/10 focus-visible:ring-indigo-500"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-700 dark:text-slate-200">Contraseña</Label>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-24 bg-white dark:bg-white/5 text-slate-900 dark:text-white placeholder:text-slate-400 border border-slate-300 dark:border-white/10 focus-visible:ring-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-2 my-auto h-9 px-3 rounded-md text-xs font-medium bg-slate-900 text-white hover:bg-slate-800 dark:bg-white/10 dark:text-white dark:hover:bg-white/20 border border-slate-900/10 dark:border-white/10 transition"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white" disabled={isLoading}>
              {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
            </Button>
            <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline dark:text-indigo-200 dark:hover:text-white"
                >
                  Olvidé mi contraseña
                </button>
            <div className="text-sm text-muted-foreground text-center">
              <span className="text-slate-600 dark:text-slate-200/80">¿No tienes una cuenta? </span>
              <span className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-200 dark:hover:text-white cursor-pointer hover:underline">
                Contacta al administrador
              </span>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default LoginPage;
